// Booked calls (owner request, PJ, 2026-10-06; migration 0027): every call that produced a meeting, newest first, for the
// whole team to listen to and learn from (what works, what doesn't), with the closer brief and the transcript. Every
// active team member sees every booked call. Filters: setter, campaign, meeting status. "Upcoming meetings" lists the
// meetings still to come, soonest first, with the meeting time in the business's zone: a closer preparing for a meeting
// another setter booked finds it here (the Meetings page shows a setter only his own).
// PJ 2026-10-20: calls with no campaign (the dial pad) have their own "No campaign" filter; a call that wasn't recorded
// says why in one sentence and its brief comes from the setter's notes.
import '../dialer/dialer.css';
import { useState } from 'preact/hooks';
import type { MeetingStatus } from '../../../src/shared/api';
import type { BookedCallListItem } from '../../../src/shared/api-booked-calls';
import { BookedCallPanel, mmss, outcomeLabel } from '../components/BookedCall';
import { Empty, ErrorBanner, fmtDateTime } from '../components/ui';
import { Segmented, Spinner, useAsync } from '../components/ui-extra';
import { endpoints } from '../dialer/endpoints';
import { useMe } from '../me';

const STATUS_LABEL: Record<MeetingStatus, string> = { booked: 'Booked', held: 'Held', no_show: 'No-show', rescheduled: 'Rescheduled', canceled: 'Canceled' };
const STATUS_TONE: Record<MeetingStatus, string> = { booked: 'chip-info', held: 'chip-ok', no_show: 'chip-bad', rescheduled: 'chip-warn', canceled: '' };

function RecordingChip(props: { item: BookedCallListItem }) {
  const r = props.item.recording;
  if (r === 'stored') return <span class="chip chip-ok">Recorded</span>;
  if (r === 'pending') return <span class="chip chip-info" title={props.item.recordingNote}>Filing</span>;
  return <span class="chip" title={props.item.recordingNote}>Not recorded</span>;
}

export function BookedCalls(_props: { params: Record<string, string> }) {
  const me = useMe();
  const admin = me.role === 'admin';
  const [setterId, setSetterId] = useState<number | ''>('');
  const [campaignId, setCampaignId] = useState<number | 'none' | ''>('');
  const [status, setStatus] = useState<MeetingStatus | ''>('');
  const [view, setView] = useState<'all' | 'upcoming'>('all');
  const [expanded, setExpanded] = useState<number | null>(null);
  const list = useAsync(() => endpoints.bookedCalls({ setterId, campaignId, status, view }), [setterId, campaignId, status, view]);
  const items = list.data?.items ?? [];
  const total = list.data?.total ?? items.length;
  const upcoming = view === 'upcoming';

  return (
    <div style="max-width:1200px">
      <div class="vd-page-head">
        <div>
          <h1 style="margin:0">Booked calls</h1>
          <p class="muted" style="margin:4px 0 0">Every call that booked a meeting. Listen as a team: what worked, what didn't. The closer brief comes from the call itself.</p>
        </div>
      </div>
      <div class="vd-filters">
        <Segmented value={view} onChange={(v) => { setView(v); setExpanded(null); }}
          options={[{ value: 'all', label: 'All booked calls' }, { value: 'upcoming', label: 'Upcoming meetings' }]} />
        <label class="row" style="gap:6px">Setter
          <select value={String(setterId)} onChange={(e) => setSetterId(e.currentTarget.value ? Number(e.currentTarget.value) : '')}>
            <option value="">Everyone</option>
            {(list.data?.setters ?? []).map((s) => <option value={String(s.id)}>{s.name}</option>)}
          </select>
        </label>
        <label class="row" style="gap:6px">Campaign
          <select value={String(campaignId)} onChange={(e) => { const v = e.currentTarget.value; setCampaignId(v === 'none' ? 'none' : v ? Number(v) : ''); }}>
            <option value="">All</option>
            {(list.data?.campaigns ?? []).map((c) => <option value={String(c.id)}>{c.name}</option>)}
            {list.data?.noCampaign || campaignId === 'none' ? <option value="none">No campaign (dial pad)</option> : null}
          </select>
        </label>
        <label class="row" style="gap:6px">Meeting
          <select value={status} onChange={(e) => setStatus(e.currentTarget.value as MeetingStatus | '')}>
            <option value="">Any status</option>
            {(Object.keys(STATUS_LABEL) as MeetingStatus[]).map((k) => <option value={k}>{STATUS_LABEL[k]}</option>)}
          </select>
        </label>
        <button class="btn btn-sm" onClick={list.reload}>Refresh</button>
      </div>
      <ErrorBanner error={list.error ? `Couldn't load booked calls: ${list.error}` : null} />
      {list.loading && !list.data ? <p class="muted"><Spinner /> Loading booked calls…</p> : null}
      {list.data && !items.length ? <Empty>{upcoming ? 'No upcoming meetings with a booked call' : 'No booked calls'}{setterId || campaignId || status ? ' match these filters' : upcoming ? '' : ' yet'}.</Empty> : null}
      {items.length ? (
        <p class="muted" style="margin:0 0 6px;font-size:13px">
          {items.length < total ? `Showing ${items.length} of ${total} (${upcoming ? 'soonest' : 'newest'} first). Narrow it with the filters.` : `${total} ${upcoming ? 'upcoming meeting' : 'booked call'}${total === 1 ? '' : 's'}, ${upcoming ? 'soonest' : 'newest'} first.`}
        </p>
      ) : null}
      {items.length ? (
        <div class="vd-table-wrap">
          <table>
            <thead><tr>{upcoming ? <th>Meeting (their time)</th> : null}<th>Call</th><th>Setter</th><th>Business</th><th>Campaign</th><th>Length</th><th>Outcome</th><th>Meeting</th><th>Recording</th><th></th></tr></thead>
            <tbody>
              {items.map((it) => {
                const open = expanded === it.meetingId;
                return [
                  <tr class={open ? 'vd-expanded' : ''}>
                    {upcoming ? <td style="white-space:nowrap"><b>{fmtDateTime(it.scheduledFor, it.tz)}</b></td> : null}
                    <td style="white-space:nowrap">{fmtDateTime(it.startedAt)}</td>
                    <td>{it.setterName}</td>
                    <td>{it.businessName}{it.city || it.stateProv ? <div class="muted" style="font-size:13px">{[it.city, it.stateProv].filter(Boolean).join(', ')}</div> : null}</td>
                    <td>{it.campaignName ?? <span class="muted">No campaign</span>}</td>
                    <td class="mono">{mmss(it.durationSec)}</td>
                    <td>{outcomeLabel(it.outcome)}</td>
                    <td>
                      <span class={`chip ${STATUS_TONE[it.meetingStatus]}`}>{STATUS_LABEL[it.meetingStatus]}</span>
                      <div class="muted" style="font-size:12px;white-space:nowrap">{upcoming ? '' : fmtDateTime(it.scheduledFor, it.tz)}{it.selfBooked ? `${upcoming ? '' : ' · '}via link` : ''}</div>
                    </td>
                    <td><RecordingChip item={it} />{it.briefStatus === 'ready' ? <span class="chip chip-info" style="margin-left:4px">Brief</span> : null}</td>
                    <td><button class="btn btn-sm" aria-expanded={open} onClick={() => setExpanded(open ? null : it.meetingId)}>{open ? 'Hide' : it.recording === 'stored' ? 'Listen' : 'Open'}</button></td>
                  </tr>,
                  open ? <tr class="vd-expanded"><td colSpan={upcoming ? 10 : 9}><BookedCallPanel meetingId={it.meetingId} call={it} showDiscord={admin} canRetry={admin} onChanged={list.reload} /></td></tr> : null,
                ];
              })}
            </tbody>
          </table>
        </div>
      ) : null}
      <p class="vd-field-hint" style="margin-top:10px">
        A booked call is the call the meeting was booked on; for a booking made through the link, the setter's last connected call to that business in the 14 days before.
        Calls with businesses in all-party-consent states aren't recorded unless the prospect said yes to it on the call; the reason is on each one, and its closer brief comes from the setter's notes.
      </p>
    </div>
  );
}
